import { supabase } from '../utils/supabase';

/**
 * Fetches the calling player's top frequent matchups from the materialised
 * player_opponent_stats table via the get_player_top_matchups RPC.
 *
 * RPC name is internal; exposed to users only as "Frequent Matchups".
 *
 * @param {object} opts
 * @param {number} [opts.limit=5]  Max matchups to return (server caps at 20).
 * @returns {Promise<Array>}
 */
export async function fetchPlayerTopMatchups({ limit = 5 } = {}) {
  const { data, error } = await supabase.rpc('get_player_top_matchups', {
    p_limit: limit,
  });

  if (error) {
    console.error('[Matchup] Error fetching top matchups:', error);
    throw error;
  }

  return data || [];
}

/**
 * Derives a display variant for a matchup card based on observable stats.
 * Never exposes internal matchup_type or scoring to the user.
 *
 * Priority: recent → closest → frequent
 *
 * @param {object} matchup  Row from get_player_top_matchups
 * @returns {'recent' | 'closest' | 'frequent'}
 */
export function getMatchupVariant(matchup) {
  const { wins, losses, total_matches, matches_30d } = matchup;
  if (matches_30d >= 3) return 'recent';
  if (wins === losses && total_matches >= 3) return 'closest';
  return 'frequent';
}

/**
 * Chip label shown on each matchup card.
 */
export const MATCHUP_VARIANT_LABELS = {
  recent:   'Recent Matchups',
  closest:  'Closest Matchup',
  frequent: 'Frequent Matchup',
};

/**
 * Neutral emoji for each matchup card variant — no combat imagery.
 */
export const MATCHUP_VARIANT_ICONS = {
  recent:   '📅',
  closest:  '⚖️',
  frequent: '👥',
};

/**
 * Tailwind colour pair [bg, text] for the icon chip — neutral palette only.
 */
export const MATCHUP_VARIANT_COLOURS = {
  recent:   ['bg-sky-100 dark:bg-sky-900/30',   'text-sky-700 dark:text-sky-400'],
  closest:  ['bg-teal-100 dark:bg-teal-900/30', 'text-teal-700 dark:text-teal-400'],
  frequent: ['bg-slate-100 dark:bg-slate-800/60', 'text-slate-600 dark:text-slate-300'],
};

/**
 * Builds the primary description line for a matchup card.
 * Language is conversational and non-competitive.
 *
 * @param {object} matchup  Row from get_player_top_matchups
 * @returns {{ headline: string, subline: string|null }}
 */
export function buildMatchupCopy(matchup) {
  const { wins, losses, total_matches, matches_30d, opponent_first_name } = matchup;
  const name = opponent_first_name || 'this player';
  const variant = getMatchupVariant(matchup);
  const record = `${wins}W – ${losses}L`;

  switch (variant) {
    case 'recent':
      return {
        headline: `You've played ${name} ${matches_30d} time${matches_30d !== 1 ? 's' : ''} this month`,
        subline: total_matches > matches_30d ? `Current record: ${record}` : null,
      };
    case 'closest':
      return {
        headline: `You and ${name} are tied ${wins}–${losses} all-time`,
        subline: null,
      };
    case 'frequent':
    default:
      return {
        headline: `You've played ${name} ${total_matches} time${total_matches !== 1 ? 's' : ''}`,
        subline: `Current record: ${record}`,
      };
  }
}
